import { Link } from 'react-router-dom';
import { categories, mainSiteUrl } from '@/mocks/homeData';
import { useState } from 'react';

interface FooterProps {
  formSubmitAddr: string;
}

export default function Footer({ formSubmitAddr }: FooterProps) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypotVal = (formData.get('website_alt') as string || '').trim();

    if (honeypotVal) {
      setStatus('success');
      setEmail('');
      return;
    }

    try {
      const res = await fetch(formSubmitAddr, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ email: formData.get('email') as string }).toString(),
      });

      const responseText = await res.text();
      let parsed: any = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (res.ok && parsed?.code === 'OK') {
        setStatus('success');
        setEmail('');
      } else {
        const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || 'Something went wrong';
        if (typeof msg === 'string' && (msg.includes('spam') || msg.includes('form data is spam'))) {
          setStatus('success');
          setEmail('');
          return;
        }
        setStatus('error');
        setErrorMsg(typeof msg === 'string' ? msg : 'Submission failed');
      }
    } catch {
      setStatus('error');
      setErrorMsg('Network error. Please try again.');
    }
  };

  const halfIdx = Math.ceil(categories.length / 2);
  const col1 = categories.slice(0, 7);
  const col2 = categories.slice(7, 11);
  const col3 = categories.slice(11);

  return (
    <footer className="bg-background-100 border-t border-background-200/50">
      <div className="w-full max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-20">
        <div className="flex flex-col lg:flex-row gap-14 lg:gap-20">
          {/* Left */}
          <div className="lg:w-[40%]">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 leading-tight">
              Stay Updated With<br />The Latest Spreadsheets
            </h2>
            <p className="mt-4 text-sm text-foreground-600 leading-relaxed max-w-md">
              Get notified when new spreadsheet resources and product guides are published.
              No spam, just curated updates from SpreadsheetHub.
            </p>

            <form onSubmit={handleSubmit} className="mt-10" data-readdy-form="">
              <label className="block text-xs font-medium text-foreground-500 mb-2 font-label">
                Email Address
              </label>
              <input
                type="email"
                name="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full max-w-sm bg-transparent border-0 border-b-2 border-foreground-300 pb-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 transition-colors"
              />
              <input
                type="text"
                name="website_alt"
                className="form-website-alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />
              <button
                type="submit"
                className="mt-6 inline-flex items-center px-6 py-2.5 rounded-full border-2 border-foreground-900 text-foreground-900 text-sm font-semibold hover:bg-foreground-900 hover:text-background-50 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                Subscribe
              </button>

              {status === 'success' && (
                <p className="mt-3 text-sm text-secondary-600 font-medium">Thanks for subscribing!</p>
              )}
              {status === 'error' && (
                <p className="mt-3 text-sm text-accent-600">{errorMsg}</p>
              )}
            </form>
          </div>

          {/* Right: Link columns */}
          <div className="lg:w-[60%] grid grid-cols-2 md:grid-cols-3 gap-8 md:gap-12">
            <div>
              <h4 className="font-label text-xs font-semibold uppercase tracking-widest text-foreground-400 mb-5">
                Categories
              </h4>
              <div className="flex flex-col gap-2.5">
                {col1.map((cat) => (
                  <a
                    key={cat.seq}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="text-sm text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {cat.name}
                  </a>
                ))}
              </div>
            </div>

            <div>
              <h4 className="font-label text-xs font-semibold uppercase tracking-widest text-foreground-400 mb-5">
                More Categories
              </h4>
              <div className="flex flex-col gap-2.5">
                {col2.map((cat) => (
                  <a
                    key={cat.seq}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="text-sm text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {cat.name}
                  </a>
                ))}
              </div>
            </div>

            <div>
              <h4 className="font-label text-xs font-semibold uppercase tracking-widest text-foreground-400 mb-5">
                Resources
              </h4>
              <div className="flex flex-col gap-2.5">
                {col3.map((cat) => (
                  <a
                    key={cat.seq}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="text-sm text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {cat.name}
                  </a>
                ))}
                <Link
                  to="/best-hipobuy-spreadsheet-guide-2026/"
                  className="text-sm text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap"
                >
                  Complete Guide
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-16 pt-8 border-t border-background-200/50 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-foreground-400">
            &copy; {new Date().getFullYear()} SpreadsheetHub. Independent research platform. Not affiliated with any shopping agent.
          </p>
          <div className="flex items-center gap-6">
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="text-xs text-foreground-400 hover:text-foreground-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              Privacy
            </a>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="text-xs text-foreground-400 hover:text-foreground-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              Terms
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}